import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
// import { datPsych,rawPsychData } from '../../mockups/Profile/payCard';

const PsychologicalAnalysis = ({data}) => {
  const [selectedMetric, setSelectedMetric] = useState('overview')
  const [emotionState, setEmotionState] = useState(null)
  const metrics_history = data[0]?.psych_data?.metrics_history



  // Transform data for visualization
  const transformedData = metrics_history?.map((item, index) => ({
    timestamp: new Date(item.last_updated).toLocaleTimeString(),
    dataPoint: index + 1,
    depressionRisk: (1 - item.depression_risk["not depression"]) * 100,
    anxietyScore: item.anxiety_indicators.anxiety_score * 100,
    stressLevel: item.stress_level * 100,
    emotionalStability: item.emotional_stability * 100,
    joy: item.primary_emotions.joy * 100,
    fear: item.primary_emotions.fear * 100,
    anger: item.primary_emotions.anger * 100,
    sadness: item.primary_emotions.sadness * 100,
    socialConnectedness: item.social_connectedness * 100,
    communicationEffectiveness: item.communication_effectiveness * 100,
    cognitiveFlexibility: item.cognitive_flexibility * 100,
    verbalFluency: item.verbal_fluency * 100,
    workingMemory: item.working_memory_indicators * 100,
    confidenceScore: item.confidence_score * 100,
    focusConsistency: item.attention_patterns.focus_consistency * 100,
    problemFocusedCoping: item.coping_strategies.problem_focused * 100,
    emotionalRegulation: item.emotional_regulation * 100,
    helpSeekingBehavior: item.help_seeking_behavior * 100,
    empathyAvg: ((item.empathy_indicators.perspective_taking + 
                  item.empathy_indicators.empathic_concern + 
                  item.empathy_indicators.emotional_responsiveness) / 3) * 100
  }));


  // Latest data for radar chart and overview
  const latestData = transformedData[transformedData.length - 1];
  const radarData = [
    { metric: 'Emotional Stability', value: latestData.emotionalStability, fullMark: 100 },
    { metric: 'Social Connection', value: latestData.socialConnectedness, fullMark: 100 },
    { metric: 'Cognitive Flexibility', value: latestData.cognitiveFlexibility, fullMark: 100 },
    { metric: 'Communication', value: latestData.communicationEffectiveness, fullMark: 100 },
    { metric: 'Confidence', value: latestData.confidenceScore, fullMark: 100 },
    { metric: 'Focus', value: latestData.focusConsistency, fullMark: 100 }
  ];

  // Emotion distribution for pie chart (latest data)
  const emotionData = [
    { name: 'Joy', value: latestData.joy, color: '#FFD700' },
    { name: 'Fear', value: latestData.fear, color: '#FF6B6B' },
    { name: 'Anger', value: latestData.anger, color: '#FF4757' },
    { name: 'Sadness', value: latestData.sadness, color: '#74B9FF' }
  ];

  function getHighestAndLowestEmotion(){
    let data = {}
    let maxValue = 0

    emotionData.map(({name,value, color}) => {
        maxValue = Math.max(value, maxValue)
        if(maxValue === value){
            data["maxEmotion"] = name
            data["maxColor"] = color
        }
        return null
    })

    data["max"] = maxValue
    setEmotionState(data) 
  }

  function getMentalHealthStatus(){
    if(latestData.emotionalStability >= 70){
        return <div>
                <p className="text-2xl font-bold text-green-700">Excellent</p>
                <p className="text-xs text-gray-600">{(latestData.emotionalStability / 100 )* 100}% no depression risk</p>
                </div>
    }else if(latestData.emotionalStability >= 55 || latestData.emotionalStability < 70){
        return <div>
                <p className="text-2xl font-bold text-green-400">Good</p>
                <p className="text-xs text-gray-600">{(latestData.emotionalStability / 100 )* 100}% low depression risk</p>
                </div>
    }else if(latestData.emotionalStability >= 40 || latestData.emotionalStability < 55){
        return <div>
                <p className="text-2xl font-bold text-yellow-300">Caution ⚠️</p>
                <p className="text-xs text-gray-600">
                {(latestData.emotionalStability / 100 )* 100}% depression risk</p>
                </div>
    }else if(latestData.emotionalStability < 40){
        return <div>
                <p className="text-2xl font-bold text-red-600">Alert 🚨</p>
                <p className="text-xs text-gray-600">{(latestData.emotionalStability / 100 )* 100}% very high depression risk</p>
                </div>
    }
    
  }


  const colors = {
    primary: '#8884d8',
    secondary: '#82ca9d',
    accent: '#ffc658',
    danger: '#ff7675',
    success: '#00b894'
  };



  useEffect(() => {
    getHighestAndLowestEmotion()
    // eslint-disable-next-line 
  },[])

  return (
    <div className="p-6">
        <div className='text-black'>
        <h3 className="text-lg font-semibold mb-4">Psychological Data</h3>
        
        {/* Key Insights Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Mental Health Status</h3>
            {latestData && getMentalHealthStatus()}
          </div>

        {emotionState &&
        <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm text-gray-500">Emotional State</h3>
            <p style={{color : emotionState?.maxColor}} className={`text-2xl font-bold`}>{emotionState?.maxEmotion}</p>
            <p className="text-xs text-gray-600">
            {parseFloat(emotionState?.max / 100 * 100).toFixed(2) } {emotionState?.maxEmotion} dominance</p>
          </div>
          }

          {/* <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Confidence Trend</h3>
            <p className="text-2xl font-bold text-blue-600">Rising ↗</p>
            <p className="text-xs text-gray-600">5% → 30% improvement</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Social Connection</h3>
            <p className="text-2xl font-bold text-orange-600">Moderate</p>
            <p className="text-xs text-gray-600">55.7% connectivity</p>
          </div> */}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {['overview', 'mental-health', 'emotions', 'cognitive', 'social'].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedMetric(tab)}
              className={`px-4 py-2 rounded-lg font-medium transition transform 
              ${selectedMetric === tab ?
                "bg-black text-white" : "bg-gray-500 text-black hover:-translate-y-1"
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1).replace('-', ' ')}
            </button>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {selectedMetric === 'overview' && (
            <>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Overall Wellbeing Trends</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={transformedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dataPoint" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="emotionalStability" stroke={colors.success} name="Emotional Stability" />
                    <Line type="monotone" dataKey="confidenceScore" stroke={colors.primary} name="Confidence" />
                    <Line type="monotone" dataKey="socialConnectedness" stroke={colors.accent} name="Social Connection" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Current Psychological Profile</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="metric" />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} />
                    <Radar name="Current State" dataKey="value" stroke={colors.primary} fill={colors.primary} fillOpacity={0.3} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {selectedMetric === 'mental-health' && (
            <>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Mental Health Indicators</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={transformedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dataPoint" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="depressionRisk" stroke={colors.danger} name="Depression Risk %" />
                    <Line type="monotone" dataKey="anxietyScore" stroke={colors.accent} name="Anxiety Score %" />
                    <Line type="monotone" dataKey="stressLevel" stroke="#e17055" name="Stress Level %" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Coping Strategies Over Time</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={transformedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dataPoint" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="problemFocusedCoping" fill={colors.success} name="Problem-Focused Coping" />
                    <Bar dataKey="helpSeekingBehavior" fill={colors.primary} name="Help-Seeking Behavior" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {selectedMetric === 'emotions' && (
            <>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Emotion Trends</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={transformedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dataPoint" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="joy" stroke="#FFD700" name="Joy %" strokeWidth={3} />
                    <Line type="monotone" dataKey="fear" stroke="#FF6B6B" name="Fear %" />
                    <Line type="monotone" dataKey="anger" stroke="#FF4757" name="Anger %" />
                    <Line type="monotone" dataKey="sadness" stroke="#74B9FF" name="Sadness %" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Current Emotion Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={emotionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({name, value}) => `${name}: ${value.toFixed(1)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {emotionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {selectedMetric === 'cognitive' && (
            <>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Cognitive Performance</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={transformedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dataPoint" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="cognitiveFlexibility" stroke={colors.primary} name="Cognitive Flexibility" />
                    <Line type="monotone" dataKey="verbalFluency" stroke={colors.secondary} name="Verbal Fluency" />
                    <Line type="monotone" dataKey="workingMemory" stroke={colors.accent} name="Working Memory" />
                    <Line type="monotone" dataKey="focusConsistency" stroke="#e17055" name="Focus Consistency" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Attention Patterns</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={transformedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dataPoint" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="focusConsistency" fill={colors.primary} name="Focus Consistency %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {selectedMetric === 'social' && (
            <>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Social & Communication Metrics</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={transformedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dataPoint" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="socialConnectedness" stroke={colors.primary} name="Social Connectedness" />
                    <Line type="monotone" dataKey="communicationEffectiveness" stroke={colors.secondary} name="Communication Effectiveness" />
                    <Line type="monotone" dataKey="empathyAvg" stroke={colors.accent} name="Empathy Indicators" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Emotional Regulation</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={transformedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dataPoint" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="emotionalRegulation" fill={colors.success} name="Emotional Regulation %" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default PsychologicalAnalysis;